import React, {
  useEffect,
  useContext,
  createContext,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

export const practiceContext = createContext();

// Wrap the hook with a provider
export const ProvidePractice = ({ children }) => {
  const inc = useProvidePractice();
  // We are providing the object returned by useProvidePractice as the value of the practiceContext
  return <practiceContext.Provider value={inc}>{children}</practiceContext.Provider>;
};

ProvidePractice.propTypes = {
  children: PropTypes.element,
};

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const usePractice = () => {
  // The context object will contain anything returned by useProvidePractice
  const context = useContext(practiceContext);
  if (context === undefined) {
    throw new Error('usePractice must be used within a ProvidePractice');
  }
  return context;
};

const initialState = {
  practice: null,
  id: null,
  version: null,
  stale: true,
  loading: false,
  modifying: false,
  error: null,
};

export const ACTIONS = Object.freeze({
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  FETCH_ERROR: 'FETCH_ERROR',
  SET_MODIFYING: 'SET_MODIFYING',
  SET_STALE: 'SET_STALE',
  SET_VERSION: 'SET_VERSION',
  SET_ID: 'SET_ID',
  RESET: 'RESET',
});


const practiceReducer = (nextState, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.FETCH_INIT:
      return {
        ...nextState,
        loading: true,
        error: null,
      };
    case ACTIONS.FETCH_SUCCESS:
      if (action.payload.deleted) {
        return {
          ...nextState,
          loading: false,
          error: 'Practice has been deleted',
          stale: false,
        };
      }

      return {
        ...nextState,
        loading: false,
        practice: action.payload,
        stale: false,
      };
    case ACTIONS.FETCH_ERROR:
      return {
        ...nextState,
        loading: false,
        practice: null,
        stale: false,
        error: 'Practice not found',
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        ...nextState,
        loading: false,
        error: action.payload,
      };
    case ACTIONS.SET_ID: {
      let stale = nextState.id === action.payload ? false : true;
      return {
        ...nextState,
        stale,
        id: action.payload,
      };
    }
    case ACTIONS.SET_VERSION:
      return {
        ...nextState,
        version: action.payload,
      };
    case ACTIONS.SET_MODIFYING:
      return {
        ...nextState,
        modifying: action.payload,
      };
    case ACTIONS.SET_STALE:
      return {
        ...nextState,
        stale: action.payload,
      };
    default:
      throw new Error(`practiceReducer this action is not supported: ${action.type}`);
  }
};

const useProvidePractice = () => {
  const controller = useRef(null);
  //practice state as a reducer
  const [practiceState, dispatch] = useReducer(practiceReducer, initialState);

  useEffect(() => {
    const {id, version, stale} = practiceState;
    if (stale && id) {
      dispatch({type: ACTIONS.FETCH_INIT})
      let mounted = true;
      controller.current = new AbortController();
      const getPractice = () => {
        axios.get(`/api/practice/get?id=${id}${version === null ? '' : `&version=${version}`}`, { signal: controller.current.signal }).then(res => {
          if (mounted) {
            dispatch({type: ACTIONS.FETCH_SUCCESS, payload: res.data});
          }
        }
        ).catch(error => {
          if (error.name !== 'CanceledError') {
            dispatch({type: ACTIONS.FETCH_ERROR, payload: error});
          }
        });
      }

      getPractice();

      // Cleanup on unmount
      return () => {
        controller.current.abort();
        mounted = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceState.stale, practiceState.id]);

  return {
    unmount: () => {
      dispatch({type: ACTIONS.RESET});
      if (practiceState.loading) {
        controller.current.abort();
      }
    },
    dispatch,
    practiceState,
  }
};
